import React from 'react';
import PropTypes from 'prop-types';
import AsyncSelect from 'react-select/async';
import { debounce, isEmpty } from 'lodash';

import ContactOption from 'components/tasks/associated_contact_selector/options/contact';

import HorizontalForm from 'components/form/horizontal_form';
import { createContact } from './api';
import { searchCompanies } from './../companies/api';
import ContactFields from './contact_fields';
import ContactCompanyForm from '../companies/form';
import Errors from 'components/form/errors';

export default class Form extends React.Component {
  static propTypes = {
    additionalFields: PropTypes.node,
    availableDatabaseOptions: PropTypes.arrayOf(PropTypes.array),
    projectId: PropTypes.number,
    toggleTypeSelector: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func
  };

  static defaultProps = {
    availableDatabaseOptions: []
  }

  state = {
    contact: {
      databaseId: '',
      projectId: '',
      email: '',
      firstName: '',
      lastName: '',
      mobilePhoneNumber: '',
      officePhoneNumber: '',
      company: {},
      jobTitle: '',
    },
    isSaving: false,
    errors: [],
    lockDatabaseSelector: false,
    showCreateCompany: false
  }

  constructor(props) {
    super(props);

    this.debouncedLoadCompanyOptions = debounce(this.loadCompanyOptions, 250);
  }

  anyAvailableDatabases = () => this.props.availableDatabaseOptions.length > 0;

  showCreateCompanyForm = () => {
    if (this.props.toggleTypeSelector) this.props.toggleTypeSelector(false);
    this.setState({ showCreateCompany: true });
  }

  cancelCreateCompany = () => {
    if (this.props.toggleTypeSelector) this.props.toggleTypeSelector(true);
    this.setState({ showCreateCompany: false, lockDatabaseSelector: false });
  }

  saveCompany = (company) => {
    this.setState((prevState) => {
      return {
        showCreateCompany: false,
        lockDatabaseSelector: true,
        contact: {
          ...prevState.contact,
          company: {
            ...company,
            id: company.associatedModelId,
            name: company.label
          }
        }
      };
    });
  }

  contact = () => {
    const { contact } = this.state;
    const { projectId } = this.props;
    return this.anyAvailableDatabases() ? contact : { ...contact, projectId };
  }

  saveContact = () => {
    this.setState({ isSaving: true, errors: [] });
    createContact(this.contact()).then((contact) => {
      this.props.onSave({
        associatedModelId: contact.id,
        label: contact.name,
        associatedModelType: contact.contactType
      });
    })
      .catch(errors => this.setState({ errors }))
      .then(() => this.setState({ isSaving: !this.anyAvailableDatabases() }));
  }

  handleChange = (e) => {
    const { name, type, checked } = e.target;
    const value = type === 'checkbox' ? checked : e.target.value;
    this.setState(prevState => ({ contact: { ...prevState.contact, [name]: value } }));
  }

  handleCompanyChange = (e) => {
    const company = {
      ...e,
      id: e.value,
      name: e.label
    };

    this.setState(prevState => ({ contact: { ...prevState.contact, company } }));
  }

  contactFieldsUpdated = (contact) => {
    this.setState(prevState => ({ contact: { ...prevState.contact, ...contact } }));
  }

  isValid = () => {
    if (this.state.isSaving) return false;

    const validNameOrEmail = this.state.contact.firstName || this.state.contact.lastName || this.state.contact.email;
    const dbSelectionValid = !this.anyAvailableDatabases() || this.state.contact.databaseId;
    return !!(dbSelectionValid && validNameOrEmail);
  }

  disableCompanySelector = () => {
    return !this.state.contact.databaseId || this.state.lockDatabaseSelector;
  }

  loadCompanyOptions = (inputValue, callback) => {
    const createCompany = {
      type: 'addCompany',
      label: 'Create Company',
      onClick: this.showCreateCompanyForm
    };

    if (inputValue) {
      searchCompanies(inputValue, this.state.contact.databaseId, (companies) => {
        const options = companies.map(company => ({ value: company.id, label: company.name }));
        callback([...options, createCompany]);
      });
    } else {
      callback([createCompany]);
    }
  }

  currentlySelectedCompany = () => {
    const { company } = this.state.contact;
    if (!isEmpty(company)) return company;
  }

  renderCreateCompanyForm() {
    return (
      <div>
        <ContactCompanyForm
          availableDatabaseOptions={this.props.availableDatabaseOptions}
          lockedDatabaseId={this.state.contact.databaseId}
          onCancel={this.cancelCreateCompany}
          onSave={this.saveCompany}
        />
      </div>
    );
  }

  renderCompanySelect() {
    if (!this.anyAvailableDatabases()) return;

    return (
      <div className="form-group">
        <label className="control-label text" style={{ height: '38px', paddingTop: '8px' }}>
          Company
        </label>
        <AsyncSelect
          className="entity-selector"
          components={{ Option: ContactOption }}
          defaultOptions
          isDisabled={this.disableCompanySelector()}
          key={JSON.stringify(this.state.contact.databaseId)}
          loadOptions={this.debouncedLoadCompanyOptions}
          placeholder="Search Companies..."
          styles={{ menu: provided => ({ ...provided, width: '70%', right: 0 }) }}
          value={this.currentlySelectedCompany()}
          onChange={this.handleCompanyChange}
        />
      </div>
    );
  }

  render() {
    if (this.state.showCreateCompany) return this.renderCreateCompanyForm();

    return (
      <div>
        <Errors errors={this.state.errors}/>

        <HorizontalForm>
          {this.anyAvailableDatabases() &&
            <React.Fragment>
              <p>This contact will be saved to the database specified below.</p>

              <HorizontalForm.Select
                disabled={this.state.lockDatabaseSelector}
                includeBlank="-- select a database --"
                label="Database"
                name="databaseId"
                options={this.props.availableDatabaseOptions}
                required
                value={this.state.contact.databaseId}
                onChange={this.handleChange}
              />
            </React.Fragment>
          }

          <ContactFields
            contact={this.state.contact}
            showCompanyName={!this.anyAvailableDatabases()}
            onChange={this.contactFieldsUpdated}
          />

          {this.renderCompanySelect()}

          {this.props.additionalFields}
        </HorizontalForm>

        <hr />

        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-ghost" name="cancel" onClick={this.props.onCancel}>Cancel</button>
          <button className="btn btn-primary" disabled={!this.isValid()} name="save" onClick={this.saveContact}>
            {this.state.isSaving ? 'Saving ...' : 'Save Contact'}
          </button>
        </div>

      </div>
    );
  }
}
